// Client-side PDF generation for spec sheets and catalogues.
// jsPDF is imported dynamically so it only loads when a user actually downloads.

import type { jsPDF } from 'jspdf';
import { CONTACT } from './contact';

const GREEN: [number, number, number] = [55, 105, 22]; // --color-primary #376916
const DARK: [number, number, number] = [18, 42, 32]; // --color-foreground #122a20
const MUTED: [number, number, number] = [111, 86, 56]; // --color-muted-foreground #6f5638
const BORDER: [number, number, number] = [223, 213, 196];
const CREAM: [number, number, number] = [246, 243, 237]; // letterbox backing for photos

const MARGIN = 48;

export interface SpecPdfData {
  productName: string;
  rows: [string, string][];
}

export interface CatalogueSpecDim {
  label: string;
  value: string;
}

export interface CatalogueSpec {
  label: string;
  code?: string;
  dims: CatalogueSpecDim[];
}

export interface CatalogueProductPdf {
  title: string;
  subtitle?: string;
  /** Hero image path (e.g. /assets/products/areca/1/2.webp). Optional  - falls back to a placeholder. */
  image?: string;
  /** Size variants with their dimensions/capacities. */
  specs: CatalogueSpec[];
}

/** One material's catalogue  - areca and bagasse are produced as SEPARATE PDFs. */
export interface CataloguePdfData {
  material: 'areca' | 'bagasse';
  /** Display name, e.g. "Areca Palm Leaf". */
  materialLabel: string;
  products: CatalogueProductPdf[];
}

function brandHeader(doc: jsPDF, eyebrow: string, title: string): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = MARGIN;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...GREEN);
  doc.text(eyebrow.toUpperCase(), MARGIN, y);

  y += 26;
  doc.setFontSize(22);
  doc.setTextColor(...DARK);
  doc.text(title, MARGIN, y);

  y += 14;
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(2);
  doc.line(MARGIN, y, MARGIN + 60, y);

  // top-right brand mark
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...GREEN);
  doc.text('FLORA DINE', pageWidth - MARGIN, MARGIN, { align: 'right' });

  return y + 28;
}

function brandFooter(doc: jsPDF, note: string): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const y = pageHeight - MARGIN;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(1);
  doc.line(MARGIN, y - 18, pageWidth - MARGIN, y - 18);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const lines = doc.splitTextToSize(note, pageWidth - MARGIN * 2);
  doc.text(lines, MARGIN, y - 6);
}

export async function downloadSpecSheetPdf(data: SpecPdfData): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  const labelWidth = contentWidth * 0.34;
  const valueX = MARGIN + labelWidth;
  const valueWidth = contentWidth - labelWidth;

  let y = brandHeader(doc, 'Flora Dine · Wholesale Specification Sheet', data.productName);

  data.rows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text(label, MARGIN, y);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    const valueLines = doc.splitTextToSize(value, valueWidth);
    doc.text(valueLines, valueX, y);

    const rowHeight = Math.max(18, valueLines.length * 13) + 8;
    y += rowHeight;

    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y - 6, pageWidth - MARGIN, y - 6);
  });

  brandFooter(
    doc,
    `Premium biodegradable foodware from sugarcane bagasse and areca palm leaf. For bulk pricing and full compliance documentation contact ${CONTACT.email} · ${CONTACT.phoneIndia} (India) / ${CONTACT.phoneUK} (UK). Specifications are indicative and may vary by configuration.`
  );

  doc.save(`FloraDine-${data.productName.replace(/[^a-z0-9]+/gi, '-')}-Spec.pdf`);
}

interface RasterImage {
  dataUrl: string;
  /** Display size in PDF points, scaled to fit the requested box (aspect preserved). */
  width: number;
  height: number;
}

// Load a (same-origin) product image and rasterize it to a JPEG data URL sized to
// fit `boxW × boxH` points. Runs in the browser only; returns null on any failure
// (missing file, decode error, non-DOM environment) so the caller can fall back.
async function loadRasterImage(
  src: string,
  boxW: number,
  boxH: number
): Promise<RasterImage | null> {
  if (!src || typeof document === 'undefined') return null;
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.decoding = 'async';
      // Guard against a request that never fires onload/onerror (stalled/offline
      // connection)  - without this the whole download would hang. On timeout we
      // fall through to the catch below and the card uses its placeholder.
      const timer = setTimeout(() => {
        el.onload = el.onerror = null;
        el.src = '';
        reject(new Error(`Timed out loading ${src}`));
      }, 8000);
      el.onload = () => {
        clearTimeout(timer);
        resolve(el);
      };
      el.onerror = () => {
        clearTimeout(timer);
        reject(new Error(`Failed to load ${src}`));
      };
      el.src = src;
    });

    const nw = img.naturalWidth || boxW;
    const nh = img.naturalHeight || boxH;
    const scale = Math.min(boxW / nw, boxH / nh);
    const dispW = Math.max(1, Math.round(nw * scale));
    const dispH = Math.max(1, Math.round(nh * scale));

    // Rasterize at 2× so the embedded JPEG stays crisp when printed.
    const dpr = 2;
    const canvas = document.createElement('canvas');
    canvas.width = dispW * dpr;
    canvas.height = dispH * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return { dataUrl: canvas.toDataURL('image/jpeg', 0.82), width: dispW, height: dispH };
  } catch {
    return null;
  }
}

// Abbreviate dimension labels so a size cell stays compact (Ø 153 mm, D 18 mm…).
const DIM_ABBR: Record<string, string> = {
  Diameter: 'Ø',
  Depth: 'D',
  Length: 'L',
  Width: 'W',
  Height: 'H',
};
function compactDim(d: CatalogueSpecDim): string {
  const abbr = DIM_ABBR[d.label];
  if (abbr) return `${abbr} ${d.value}`;
  if (/^(dimensions|size)$/i.test(d.label)) return d.value; // value already self-describing
  return `${d.label} ${d.value}`;
}

// A SINGLE-MATERIAL catalogue: one product per row  - hero image + title on the
// left, then every size as a compact spec cell across the right. Areca and
// bagasse are produced as separate PDFs (never merged).
export async function downloadCataloguePdf(data: CataloguePdfData): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;

  // ── Row geometry ───────────────────────────────────────────────────────────
  const imgColW = 116;
  const imgBoxW = 104;
  const imgBoxH = 76;
  const colGap = 14;
  const specsX = MARGIN + imgColW + colGap;
  const specsW = contentWidth - imgColW - colGap;
  const CELLS_PER_LINE = 4;
  const cellGap = 6;
  const cellW = (specsW - cellGap * (CELLS_PER_LINE - 1)) / CELLS_PER_LINE;
  const titleLineH = 11;
  const labelLineH = 9;
  const dimLineH = 8;
  const ROW_PAD = 16;
  const footerReserve = 92;
  const contentBottom = pageHeight - footerReserve;

  // jsPDF's standard fonts use WinAnsi/CP1252, which lacks the prime marks
  // (′ ″) used in size labels  - normalize them so they don't render as wrong
  // glyphs (the web keeps the nicer typographic primes).
  const safe = (s: string) => s.replace(/′/g, "'").replace(/″/g, '"');
  const products = data.products.map((p) => ({
    image: p.image,
    title: safe(p.title),
    subtitle: p.subtitle ? safe(p.subtitle) : undefined,
    specs: p.specs.map((s) => ({
      label: safe(s.label),
      code: s.code ? safe(s.code) : undefined,
      dims: s.dims.map((d) => ({ label: d.label, value: safe(d.value) })),
    })),
  }));

  const rasters = await Promise.all(
    products.map((p) => (p.image ? loadRasterImage(p.image, imgBoxW, imgBoxH) : null))
  );

  const eyebrow = `Flora Dine · ${data.materialLabel} Catalogue`;
  let y = brandHeader(doc, eyebrow, data.materialLabel);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10.5);
  doc.setTextColor(...MUTED);
  const intro = doc.splitTextToSize(
    `Every ${data.materialLabel.toLowerCase()} line with its full size range and indicative dimensions. Plastic-free, certified, and built for bulk and export supply.`,
    contentWidth
  );
  doc.text(intro, MARGIN, y);
  y += intro.length * 14 + 14;

  // Measure a product row up front so we can page-break before drawing it.
  const measureRow = (product: CatalogueProductPdf) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const titleLines = (doc.splitTextToSize(product.title, imgColW - 2) as string[]).slice(0, 2);
    const maxDims = product.specs.reduce((m, s) => Math.max(m, s.dims.length), 0);
    const hasCode = product.specs.some((s) => !!s.code);
    const cellH = 8 + 2 * labelLineH + (maxDims + (hasCode ? 1 : 0)) * dimLineH + 4;
    const specLines = Math.max(1, Math.ceil(product.specs.length / CELLS_PER_LINE));
    const specsH = specLines * cellH + (specLines - 1) * cellGap;
    const imageBlockH = imgBoxH + 12 + titleLines.length * titleLineH + (product.subtitle ? 10 : 0);
    return { titleLines, cellH, rowH: Math.max(specsH, imageBlockH) + ROW_PAD };
  };

  const drawCell = (spec: CatalogueSpec, cx: number, cy: number, cellH: number) => {
    doc.setFillColor(...CREAM);
    doc.roundedRect(cx, cy, cellW, cellH, 3, 3, 'F');
    // Size label (clamped to 2 lines).
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...DARK);
    let labelLines = doc.splitTextToSize(spec.label, cellW - 10) as string[];
    if (labelLines.length > 2) {
      labelLines = labelLines.slice(0, 2);
      labelLines[1] = `${labelLines[1].replace(/[\s.,;:·-]+$/, '')}…`;
    }
    doc.text(labelLines, cx + 5, cy + 9);
    // Dimensions, then the optional code  - start after a fixed 2-line label slot.
    let dy = cy + 9 + 2 * labelLineH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    for (const d of spec.dims) {
      doc.text(compactDim(d), cx + 5, dy);
      dy += dimLineH;
    }
    if (spec.code) {
      doc.setTextColor(...GREEN);
      doc.text(spec.code, cx + 5, dy);
    }
  };

  const drawProduct = (
    product: CatalogueProductPdf,
    raster: RasterImage | null,
    top: number,
    m: ReturnType<typeof measureRow>
  ) => {
    doc.setFillColor(...CREAM);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.5);
    doc.roundedRect(MARGIN, top, imgBoxW, imgBoxH, 5, 5, 'FD');
    if (raster) {
      const ox = MARGIN + (imgBoxW - raster.width) / 2;
      const oy = top + (imgBoxH - raster.height) / 2;
      doc.addImage(raster.dataUrl, 'JPEG', ox, oy, raster.width, raster.height);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text('Photo on request', MARGIN + imgBoxW / 2, top + imgBoxH / 2, { align: 'center' });
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(m.titleLines, MARGIN, top + imgBoxH + 12);
    if (product.subtitle) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text(
        (doc.splitTextToSize(product.subtitle, imgColW - 2) as string[])[0] ?? '',
        MARGIN,
        top + imgBoxH + 12 + m.titleLines.length * titleLineH
      );
    }

    if (product.specs.length === 0) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text('Sizes available on request', specsX, top + 14);
      return;
    }
    product.specs.forEach((spec, k) => {
      const col = k % CELLS_PER_LINE;
      const line = Math.floor(k / CELLS_PER_LINE);
      drawCell(spec, specsX + col * (cellW + cellGap), top + line * (m.cellH + cellGap), m.cellH);
    });
  };

  for (let i = 0; i < products.length; i++) {
    const product = products[i];
    const m = measureRow(product);
    if (y + m.rowH > contentBottom) {
      doc.addPage();
      y = MARGIN + 24;
    }
    drawProduct(product, rasters[i], y, m);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, y + m.rowH - 8, pageWidth - MARGIN, y + m.rowH - 8);
    y += m.rowH;
  }

  // ── Per-page chrome: running header (p2+), footer, page numbers ─────────────
  const pageCount = doc.getNumberOfPages();
  const note = `For full specifications, bulk pricing, and MOQs contact ${CONTACT.email} · ${CONTACT.phoneIndia} (India) / ${CONTACT.phoneUK} (UK). Dimensions are indicative and may vary by configuration.`;
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    if (p > 1) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...GREEN);
      doc.text(eyebrow.toUpperCase(), MARGIN, MARGIN - 16);
      doc.text('FLORA DINE', pageWidth - MARGIN, MARGIN - 16, { align: 'right' });
    }
    brandFooter(doc, note);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Page ${p} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 22, { align: 'right' });
  }

  doc.save(`FloraDine-${data.material}-Catalogue.pdf`);
}
